import { useRef } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const STEPS = [
  {
    num: '01',
    title: 'Consultation & Site Evaluation',
    desc: 'We visit your property, listen to your vision, assess the terrain, and identify opportunities that others miss.',
    icon: 'ri-discuss-line',
  },
  {
    num: '02',
    title: 'Custom Design & Planning',
    desc: 'Our designers craft a tailored landscape plan with detailed drawings, plant selections, and material specs.',
    icon: 'ri-draft-line',
  },
  {
    num: '03',
    title: 'Material Selection & Scheduling',
    desc: 'We source premium materials and build a clear project timeline — so you always know what\'s next.',
    icon: 'ri-calendar-check-line',
  },
  {
    num: '04',
    title: 'Professional Installation',
    desc: 'Expert crews execute your vision with precision craftsmanship, respecting your property throughout the build.',
    icon: 'ri-tools-line',
  },
  {
    num: '05',
    title: 'Final Walkthrough & Optimization',
    desc: 'We walk the completed project with you, fine-tune every detail, and ensure you\'re completely satisfied.',
    icon: 'ri-check-double-line',
  },
];

export default function ProcessSection() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.1 });

  return (
    <section className="bg-charcoal-900 py-28 relative overflow-hidden">
      {/* Ambient bg image */}
      <div className="absolute inset-0 opacity-8">
        <img
          src="https://public.readdy.ai/ai/img_res/4383f9236cb29f690c81ca4e77a443ef.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-charcoal-900/90" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8">
        {/* Header */}
        <div
          ref={ref}
          className={`grid md:grid-cols-2 gap-16 items-center mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <p className="font-sans text-stone-500 text-xs tracking-[0.25em] uppercase mb-4">How We Work</p>
            <h2 className="font-serif text-white font-semibold leading-tight" style={{ fontSize: 'clamp(32px, 4vw, 52px)' }}>
              A Proven Process for<br />
              <em className="italic text-stone-400">Exceptional Results</em>
            </h2>
          </div>
          <div>
            <p className="font-sans text-stone-400 text-base leading-relaxed">
              We've refined our approach over 19 years to deliver every project on time, on budget, and beyond expectations. Here's exactly how we work from first call to final reveal.
            </p>
          </div>
        </div>

        {/* Steps grid */}
        <div className="grid md:grid-cols-5 gap-0 relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-10 left-0 right-0 h-px bg-gradient-to-r from-transparent via-stone-700 to-transparent" />

          {STEPS.map((step, i) => (
            <ProcessStep key={step.num} step={step} delay={i * 120} />
          ))}
        </div>

        {/* Bottom image */}
        <div className="mt-16 w-full h-64 rounded-lg overflow-hidden relative">
          <img
            src="https://brainardsgreenscapes.com/assets/images/after2.jpeg"
            alt="Brainard's Greenscapes professional installation team"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/50 via-transparent to-charcoal-900/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <a
              href="#consultation"
              onClick={(e) => { e.preventDefault(); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center gap-3 bg-forest-500 hover:bg-forest-400 text-white font-sans font-semibold text-sm px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              Start My Project Today
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProcessStep({ step, delay }: { step: typeof STEPS[0]; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`flex flex-col items-center text-center px-4 relative transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Number circle */}
      <div className="relative z-10 w-20 h-20 flex items-center justify-center rounded-full border border-stone-700 bg-charcoal-900 mb-6">
        <div className="w-12 h-12 flex items-center justify-center bg-forest-700 rounded-full">
          <i className={`${step.icon} text-stone-300 text-lg`} />
        </div>
        <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center bg-charcoal-800 rounded-full border border-stone-700">
          <span className="font-sans text-stone-500 text-[9px] font-bold">{step.num}</span>
        </div>
      </div>

      <h3 className="font-serif text-white text-base font-semibold mb-3 leading-snug">{step.title}</h3>
      <p className="font-sans text-stone-500 text-xs leading-relaxed">{step.desc}</p>
    </div>
  );
}
